import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Vault } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function Login() {
  const { t } = useLang();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const { slug: pathSlug } = useParams();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("shop");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [slug, setSlug] = useState(pathSlug || params.get("shop") || "");
  const [detectedShop, setDetectedShop] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (pathSlug || params.get("shop")) return;
    const host = window.location.hostname;
    const parts = host.split(".");
    if (parts.length >= 3 && parts[0] !== "www") {
      const candidate = parts[0];
      API.get(`/public/shops/${candidate}`)
        .then((r) => {
          if (r.data.exists) {
            setSlug(candidate);
            setDetectedShop(r.data.name);
          }
        })
        .catch(() => {});
    }
  }, [pathSlug, params]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await API.post("/auth/login", {
        email,
        password,
        shop_slug: mode === "shop" && slug ? slug : undefined,
      });
      setUser(data);
      navigate(data.role === "super_admin" ? "/admin" : "/app");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] hero-grid-bg flex flex-col" data-testid="login-page">
      <div className="flex items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-2 text-white">
          <Vault className="h-6 w-6 text-emerald-400" />
          <span className="font-heading font-bold text-lg">{t("brand")}</span>
        </Link>
        <LanguageSwitcher dark />
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 animate-fade-up">
          <h1 className="font-heading text-2xl font-bold text-slate-900">{t("auth.welcome")}</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">{t("auth.login_sub")}</p>
          <Tabs value={mode} onValueChange={setMode} className="mb-6">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="shop" data-testid="login-tab-shop">{t("auth.shop_login")}</TabsTrigger>
              <TabsTrigger value="admin" data-testid="login-tab-admin">{t("auth.admin_login")}</TabsTrigger>
            </TabsList>
          </Tabs>
          <form onSubmit={submit} className="space-y-4">
            {mode === "shop" && (
              <div>
                <Label htmlFor="shop-slug">{t("landing.shop_id")}</Label>
                <Input
                  id="shop-slug"
                  data-testid="login-shop-slug-input"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="my-shop"
                  className="mt-1"
                />
                {detectedShop && <p className="text-xs text-emerald-600 mt-1">{detectedShop}</p>}
              </div>
            )}
            <div>
              <Label htmlFor="email">{t("landing.email")}</Label>
              <Input id="email" data-testid="login-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">{t("landing.password")}</Label>
              <Input id="password" data-testid="login-password-input" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} className="mt-1" />
            </div>
            <Button data-testid="login-submit-button" type="submit" disabled={busy}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11">
              {busy ? t("common.loading") : t("auth.signin")}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-emerald-700 hover:underline">
              {t("auth.forgot")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
